/**
 * The two fixed question sets staff fill in on a candidate and on a client.
 *
 * One list per audience, and the wording lives here rather than in the components so a reword
 * is a single edit rather than a hunt through two workspaces.
 *
 * **`key` is an identifier, not the question.** `Employer.onboardingSteps` stores its display
 * labels as keys, which means rewording a step silently orphans every stored answer. These keys
 * are short and stable, and the prose above them can change freely.
 *
 * Answers are stored as `[{ key, value }]` on the record — `JobSeeker.screeningAnswers` and
 * `Company.discoveryAnswers` — and both are stripped for non-internal callers by the backend.
 * Neither set is ever rendered on a public or employer surface.
 */

export interface TopQuestion {
  /** Stable storage key. Never change one of these without migrating stored answers. */
  key: string;
  /** The question as staff read it. Safe to reword. */
  question: string;
  /** One line of guidance under the question, where the ask is not self-evident. */
  hint?: string;
}

/** Asked of the candidate by the recruiter, on a screening call. */
export const TOP_QUESTIONS_JOBSEEKER: TopQuestion[] = [
  {
    key: 'motivation',
    question: 'Why are you looking for a job change right now?',
    hint: 'Genuine motivation versus casually exploring.',
  },
  {
    key: 'tenure',
    question: 'Why did you leave each of your last 2-3 companies?',
    hint: 'Ask the reason behind every stint under 12 months.',
  },
  {
    key: 'compensation',
    question: 'Current, expected, and minimum acceptable CTC?',
    hint: 'Capture all three. The minimum is the one that decides an offer.',
  },
  {
    key: 'availability',
    question: 'Notice period, and earliest realistic joining date?',
    hint: 'Ask for a realistic date, not only the official notice period.',
  },
  {
    key: 'offers',
    question:
      'If offered this role at the discussed salary, how likely are you to join? Interviewing or holding offers elsewhere?',
    hint: 'Surfaces offer-shopping and dropouts before the profile is submitted.',
  },
];

/** Asked of the hiring manager by Sales, when taking the brief. */
export const TOP_QUESTIONS_COMPANY: TopQuestion[] = [
  {
    key: 'nonNegotiables',
    question: 'What are the 3-5 non-negotiable requirements for this role?',
    hint: 'Separate must-haves from nice-to-haves: experience, industry, skills, location.',
  },
  {
    key: 'dealBreakers',
    question: 'What would make you immediately reject a candidate?',
    hint: 'Job hopping, wrong industry, salary mismatch, gaps, communication.',
  },
  {
    key: 'salaryRange',
    question: 'Approved salary range, and how flexible for an exceptional candidate?',
    hint: 'Minimum, target and maximum, plus incentives or commission.',
  },
  {
    key: 'idealHire',
    question: 'What would an ideal hire look like?',
    hint: 'Previous brands, experience, skills, team fit, success at 6-12 months.',
  },
  {
    key: 'process',
    question: 'What is your interview process and hiring timeline?',
    hint: 'Rounds, decision-makers, assessments, target joining date, feedback speed.',
  },
];
